import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export interface ScannedItem {
    id: string;
    name: string;
    price: number;
    quantity: number;
    included: boolean;
    category: 'Food' | 'Drink' | 'Other';
}

export interface ScannedReceiptResult {
    items: ScannedItem[];
    total: number;
    tax: number;
    serviceCharge: number;
    discounts: { name: string; amount: number }[];
}

/**
 * Run receipt scan using the Gemini Cloud Function
 */
export async function scanReceipt(
    imageFile: File | Blob,
    onProgress?: (progress: number) => void
): Promise<ScannedReceiptResult> {
    if (onProgress) {
        onProgress(10); // Indicate starting
    }

    const compressedBlob = await compressImage(imageFile, 1500, 0.7);
    const base64Data = await blobToBase64(compressedBlob);
    
    // The base64Data includes the data URI prefix (e.g. data:image/jpeg;base64,...), 
    // we need to strip it before sending to the function if we use inlineData format in Gemini.
    // However, we can just split by comma
    const parts = base64Data.split(',');
    const base64Image = parts[1] ?? parts[0] ?? ''; 
    const mimeType = imageFile.type || 'image/jpeg';

    if (onProgress) {
        onProgress(40); // Image encoded, sending to server
    }



    const scanReceiptImage = httpsCallable<
        { base64Image: string, mimeType: string }, 
        { items: Omit<ScannedItem, 'id' | 'included'>[], total?: number, tax?: number, serviceCharge?: number, discounts?: { name: string; amount: number }[] }
    >(functions, 'scanReceiptImage');
    
    const response = await scanReceiptImage({
        base64Image,
        mimeType
    });

    if (onProgress) {
        onProgress(90); // Got response
    }

    const items = response.data.items;
    
    const parsedItems: ScannedItem[] = items.map((item) => {
        const validCategories = ['Food', 'Drink', 'Other'] as const;
        const rawCat = item.category as string;
        const category: 'Food' | 'Drink' | 'Other' = validCategories.includes(rawCat as 'Food' | 'Drink' | 'Other')
            ? (rawCat as 'Food' | 'Drink' | 'Other')
            : 'Food';
        return {
            id: Math.random().toString(36).substring(2, 9),
            name: item.name,
            price: item.price,
            quantity: item.quantity || 1,
            included: true,
            category,
        };
    });

    if (onProgress) {
        onProgress(100); // Done
    }

    return {
        items: parsedItems,
        total: response.data.total ?? 0,
        tax: response.data.tax ?? 0,
        serviceCharge: response.data.serviceCharge ?? 0,
        discounts: response.data.discounts ?? []
    };
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => { resolve(reader.result as string); };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Create a cropped image Blob from a source image and crop coordinates.
 */
export async function getCroppedImg(
    imageSrc: string,
    pixelCrop: { x: number; y: number; width: number; height: number }
): Promise<Blob> {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context');
    }

    // Set width/height to the cropped size
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Canvas is empty'));
                return;
            }
            resolve(blob);
        }, 'image/jpeg');
    });
}

function createImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => { resolve(image); });
        image.addEventListener('error', () => { reject(new Error('Failed to load image')); });
        image.src = url;
    });
}

/**
 * Compress an image by resizing it to a maximum width while maintaining aspect ratio,
 * and converting it to JPEG with the specified quality.
 */
export async function compressImage(
    fileOrBlob: Blob,
    maxWidth = 1500,
    quality = 0.7
): Promise<Blob> {
    const url = URL.createObjectURL(fileOrBlob);
    try {
        const image = await createImage(url);
        
        let width = image.width;
        let height = image.height;
        
        if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('No 2d context');
        
        ctx.drawImage(image, 0, 0, width, height);
        
        return await new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }
                resolve(blob);
            }, 'image/jpeg', quality);
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}
